import React, { useState } from "react";
import { Table, Spinner } from "react-bootstrap";
import { Paper } from "@material-ui/core";
import { useAlert } from "react-alert";
import { baseUrl } from "../../../../services/config";

const STATUS_COLORS = {
  present: { bg: "#d4edda", color: "#155724", label: "Present" },
  late_coming: { bg: "#fff3cd", color: "#856404", label: "Late Coming" },
  missing: { bg: "#f8d7da", color: "#721c24", label: "Missing" },
  absent: { bg: "#f5c6cb", color: "#721c24", label: "Absent" },
  off_day: { bg: "#e2e3e5", color: "#383d41", label: "Off Day" },
  casual_leave: { bg: "#cce5ff", color: "#004085", label: "Casual Leave" },
  sick_leave: { bg: "#d1ecf1", color: "#0c5460", label: "Sick Leave" },
  short_leave: { bg: "#e2e3e5", color: "#383d41", label: "Short Leave" },
  annual_leave: { bg: "#d1ecf1", color: "#0c5460", label: "Annual Leave" },
};

const SOURCE_LABELS = {
  biometric: "Biometric",
  system: "System",
  system_and_biometric: "Both",
  biometric_incomplete: "Bio (incomplete)",
  calendar: "Calendar",
  weekly_rule: "Weekly Rule",
  none: "—",
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_COLORS[status] || { bg: "#e2e3e5", color: "#383d41", label: status };
  return (
    <span
      style={{
        background: cfg.bg,
        color: cfg.color,
        padding: "3px 10px",
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {cfg.label}
    </span>
  );
};

const SummaryCard = ({ label, value, color }) => (
  <div
    style={{
      flex: "1 1 90px",
      minWidth: 90,
      background: "#f8f9fa",
      border: "1px solid #dee2e6",
      borderRadius: 8,
      padding: "10px 14px",
      textAlign: "center",
    }}
  >
    <div style={{ fontSize: 22, fontWeight: 700, color: color || "#333" }}>{value}</div>
    <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>{label}</div>
  </div>
);

const EmployeeAttendanceReport = () => {
  const authtoken = localStorage.getItem("userToken");
  const alert = useAlert();

  const today = new Date();
  const defaultMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;

  const [empCode, setEmpCode] = useState("");
  const [salaryMonth, setSalaryMonth] = useState(defaultMonth);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  const fetchReport = async () => {
    const code = empCode.trim();
    if (!code) {
      alert.show("Please enter an employee code.");
      return;
    }
    setLoading(true);
    setData(null);
    setExpandedRows({});
    try {
      const params = new URLSearchParams({
        emp_code: code,
        salary_month: salaryMonth + "-01",
      });
      const res = await fetch(
        `${baseUrl}/attendance/employee-monthly-report?${params}`,
        { headers: { Authorization: `Bearer ${authtoken}` } }
      );
      const json = await res.json();
      if (json.metadata?.success) {
        setData(json.payload);
      } else {
        const msg = json.metadata?.message;
        const errorText =
          msg && typeof msg === "object"
            ? Object.values(msg).flat().join(" ")
            : typeof msg === "string"
            ? msg
            : "No data found.";
        alert.error(errorText);
      }
    } catch (err) {
      console.log(err);
      alert.error("Failed to fetch report.");
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (date) =>
    setExpandedRows((prev) => ({ ...prev, [date]: !prev[date] }));

  const handleKeyDown = (e) => {
    if (e.key === "Enter") fetchReport();
  };

  const emp = data?.employee;
  const summary = data?.summary;

  return (
    <Paper style={{ padding: 20, marginTop: 10 }}>
      <h4 className="mb-4">Employee Attendance Report</h4>

      <div className="row mb-3 align-items-end">
        <div className="col-md-3">
          <label className="form-control-label"><b>Employee Code</b></label>
          <input
            className="form-control"
            placeholder="e.g. EMP-001"
            value={empCode}
            onChange={(e) => { setEmpCode(e.target.value); setData(null); }}
            onKeyDown={handleKeyDown}
          />
        </div>

        <div className="col-md-2">
          <label className="form-control-label"><b>Month</b></label>
          <input
            type="month"
            className="form-control"
            value={salaryMonth}
            onChange={(e) => { setSalaryMonth(e.target.value); setData(null); }}
          />
        </div>

        <div className="col-md-2">
          <button
            onClick={fetchReport}
            disabled={loading}
            className="btn btn-success button_blue w-100"
          >
            {loading ? <Spinner size="sm" animation="border" /> : "Fetch"}
          </button>
        </div>
      </div>

      {emp && (
        <div
          style={{
            background: "#f8f9fa",
            borderRadius: 6,
            padding: "10px 16px",
            marginBottom: 14,
            display: "flex",
            gap: 24,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <span><b>Name:</b> {emp.full_name}</span>
          <span><b>Code:</b> {emp.emp_code}</span>
          <span><b>Designation:</b> {emp.designation || "—"}</span>
          <span><b>Campus:</b> {emp.campus || "—"}</span>
          <span>
            <b>Biometric ID:</b>{" "}
            {emp.biometric_device_user_id || <i className="text-muted">Not linked</i>}
          </span>
          <span><b>Rule:</b> {emp.attendance_rule || <i className="text-muted">None</i>}</span>
          <span><b>Remaining Leaves:</b> {emp.remaining_leaves ?? "—"}</span>
          <span><b>Month:</b> {data.salary_month}</span>
        </div>
      )}

      {summary && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18 }}>
          <SummaryCard label="Total Days" value={summary.total_days} />
          <SummaryCard label="Off Days" value={summary.off_days} color="#383d41" />
          <SummaryCard label="Working Days" value={summary.working_days} />
          <SummaryCard label="Present" value={summary.present} color="#155724" />
          <SummaryCard label="Late" value={summary.late_coming} color="#856404" />
          <SummaryCard label="Absent" value={summary.absent} color="#721c24" />
          <SummaryCard label="Leave" value={summary.leave} color="#004085" />
          <SummaryCard label="Missing" value={summary.missing} color="#721c24" />
        </div>
      )}

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : data ? (
        <Table responsive bordered size="sm">
          <thead className="tablehead">
            <tr className="tableheadtr text-center">
              <th className="background-color-primary tableHEadNAme" style={{ width: 28 }} />
              <th className="background-color-primary tableHEadNAme">Date</th>
              <th className="background-color-primary tableHEadNAme">Day</th>
              <th className="background-color-primary tableHEadNAme">Status</th>
              <th className="background-color-primary tableHEadNAme">Source</th>
              <th className="background-color-primary tableHEadNAme">System Status</th>
              <th className="background-color-primary tableHEadNAme">First In</th>
              <th className="background-color-primary tableHEadNAme">Last Out</th>
              <th className="background-color-primary tableHEadNAme">Logs</th>
              <th className="background-color-primary tableHEadNAme">Note</th>
            </tr>
          </thead>
          <tbody>
            {!data.daily_details?.length ? (
              <tr>
                <td colSpan={10} className="text-center text-muted py-4">
                  No attendance data available.
                </td>
              </tr>
            ) : (
              data.daily_details.map((day) => {
                const hasLogs = day.biometric?.logs?.length > 0;
                const isExpanded = expandedRows[day.date];

                return (
                  <React.Fragment key={day.date}>
                    <tr style={day.is_off_day ? { background: "#f0f0f0" } : undefined}>
                      <td className="text-center" style={{ verticalAlign: "middle" }}>
                        {hasLogs && (
                          <button
                            onClick={() => toggleRow(day.date)}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              fontSize: 12,
                              color: "#007bff",
                              padding: 0,
                            }}
                          >
                            {isExpanded ? "▲" : "▼"}
                          </button>
                        )}
                      </td>
                      <td style={{ whiteSpace: "nowrap", fontWeight: 500 }}>{day.date}</td>
                      <td style={{ fontSize: 12 }}>{day.day_name}</td>
                      <td><StatusBadge status={day.final_status} /></td>
                      <td style={{ fontSize: 12 }}>
                        {SOURCE_LABELS[day.source] || day.source || "—"}
                      </td>
                      <td>
                        {day.system?.status ? (
                          <StatusBadge status={day.system.status} />
                        ) : (
                          <span className="text-muted" style={{ fontSize: 12 }}>—</span>
                        )}
                      </td>
                      <td style={{ fontSize: 12 }}>{day.biometric?.first_in || "—"}</td>
                      <td style={{ fontSize: 12 }}>{day.biometric?.last_out || "—"}</td>
                      <td className="text-center" style={{ fontSize: 12 }}>
                        {day.biometric?.log_count || 0}
                      </td>
                      <td style={{ fontSize: 11, color: "#555", maxWidth: 200 }}>
                        {day.note || "—"}
                      </td>
                    </tr>

                    {isExpanded && hasLogs && (
                      <tr>
                        <td colSpan={10} style={{ padding: "4px 24px 8px", background: "#f9f9ff" }}>
                          <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                            <thead>
                              <tr style={{ background: "#eef" }}>
                                <th style={{ padding: "4px 8px", border: "1px solid #ddd" }}>#</th>
                                <th style={{ padding: "4px 8px", border: "1px solid #ddd" }}>Recorded At</th>
                                <th style={{ padding: "4px 8px", border: "1px solid #ddd" }}>Punch</th>
                              </tr>
                            </thead>
                            <tbody>
                              {day.biometric.logs.map((log, i) => (
                                <tr key={i}>
                                  <td style={{ padding: "3px 8px", border: "1px solid #ddd" }}>{i + 1}</td>
                                  <td style={{ padding: "3px 8px", border: "1px solid #ddd", whiteSpace: "nowrap" }}>
                                    {log.recorded_at}
                                  </td>
                                  <td style={{ padding: "3px 8px", border: "1px solid #ddd" }}>{log.punch}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </Table>
      ) : (
        !loading && (
          <div className="text-center text-muted py-5">
            Enter an employee code and month, then click <b>Fetch</b> or press <b>Enter</b>.
          </div>
        )
      )}
    </Paper>
  );
};

export default EmployeeAttendanceReport;
